// @ts-nocheck

import { createSelector } from 'reselect';

import { selectHasMissionData } from '../../modules/missions/selectors/banner';
import {
  getCategoryIdFromProps,
  getIsBetFilteredFromProps,
  getListBetIdsFromProps,
  getMatchIdFromProps,
  getMissionIdFromProps,
  getPromotionImageFromProps,
  getPromotionUrlFromProps,
  getPromotionWamUrlFromProps,
  getSeparatorTypeFromProps,
} from '../../selectors/_propsGetters_';
import {
  selectEntitiesMatchesEntity,
  selectEntitiesMatchesEntityIsOutright,
  selectEntitiesMatchesEntityMainBetId,
  selectEntitiesMatchesEntityPeriodName,
} from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesTournamentsEntity } from '../../selectors/entities/tournaments/entitiesTournamentsPrimary.selector';
import {
  selectMatchCompetitor1NbRedCards,
  selectMatchCompetitor2NbRedCards,
  selectIsMatchFavorite,
  selectIsMatchLineUpAvailableWithPartialData,
  selectMatchImage,
  selectIsMatchLive,
  selectMatchCardMode,
} from '../../selectors/match.selector';
import { selectUserConnected } from '../../selectors/user/userPrimary.selector';
import { MatchEntity } from '../../states/entities/matches';
import { CardModes, type CardMode } from '../../types/common';
import { type SelectorMaker, type Selector } from '../../types/selectors';
import { type SeparatorType } from '../../types/separator';
import { createShallowEqualSelector } from '../../utils/reselect';

type RowFactoryPropsType = {
  separatorType?: SeparatorType;
  promotionImage?: string;
  promotionUrl?: string;
  promotionWamUrl?: string;
  promotionMissionId?: string;
  categoryId?: number;
  tournamentId?: number;
  cardMode?: CardMode;
  isEventLive?: boolean;
  isOutright?: boolean;
  betsData?: Array<{ betId: number; isMultichoice: boolean }>;
  image?: string | null;
  showFavorite?: boolean;
  isFavorite?: boolean;
  periodName?: string;
  to?: string;
  competitor1NbRedCards?: number;
  competitor2NbRedCards?: number;
  areMatchLineUpsAvailable?: boolean;
};

const makeRowFactorySelector: SelectorMaker<RowFactoryPropsType> = () => {
  const selectBetsData: Selector<Array<{ betId: number; isMultichoice: boolean }>> = createShallowEqualSelector(
    [selectEntitiesMatchesEntityMainBetId, getListBetIdsFromProps, getIsBetFilteredFromProps],
    (mainBet, listBetIds, isBetFiltered) =>
      (isBetFiltered ? listBetIds : [mainBet]).map((betId) => ({ betId, isMultichoice: false })),
  );

  const selectHasPlayerStatsTracker: Selector<boolean> = createSelector(
    [selectEntitiesMatchesEntity, selectEntitiesTournamentsEntity],
    (match, tournament) => (tournament ? MatchEntity.hasPlayerStatsTracker(match, tournament) : false),
  );

  return createShallowEqualSelector(
    [
      selectBetsData,
      selectIsMatchLive,
      selectEntitiesMatchesEntityIsOutright,
      selectEntitiesMatchesEntityPeriodName,
      selectHasPlayerStatsTracker,
      selectMatchImage,
      selectUserConnected,
      selectIsMatchFavorite,
      selectMatchCardMode,
      selectHasMissionData,
      selectMatchCompetitor1NbRedCards,
      selectMatchCompetitor2NbRedCards,
      selectIsMatchLineUpAvailableWithPartialData,
      getCategoryIdFromProps,
      getMatchIdFromProps,
      getSeparatorTypeFromProps,
      getPromotionImageFromProps,
      getPromotionUrlFromProps,
      getPromotionWamUrlFromProps,
      getMissionIdFromProps,
    ],
    (
      betsData,
      isEventLive,
      isOutright,
      periodName,
      hasPlayerStatsTracker,
      image,
      connected,
      isFavorite,
      boostedCardMode,
      hasMissionData,
      competitor1NbRedCards,
      competitor2NbRedCards,
      areMatchLineUpsAvailable,
      categoryId,
      matchId,
      separatorType,
      promotionImage,
      promotionUrl,
      promotionWamUrl,
      promotionMissionId,
    ) => {
      if (separatorType) {
        return {
          separatorType,
          promotionImage,
          promotionUrl,
          promotionWamUrl,
          promotionMissionId: hasMissionData ? promotionMissionId : undefined,
          categoryId,
        };
      }
      if (promotionImage) {
        return {
          cardMode: CardModes.PROMO,
          promotionImage,
          promotionUrl,
          promotionWamUrl,
        };
      }
      if (promotionMissionId) {
        return {
          cardMode: CardModes.MISSIONS_BANNER,
          promotionMissionId: hasMissionData ? promotionMissionId : undefined,
        };
      }
      const to = `/match/${matchId}`;

      const showFavorite = connected;

      return {
        isEventLive,
        isOutright,
        betsData,
        image,
        showFavorite,
        isFavorite,
        cardMode: boostedCardMode,
        periodName,
        to,
        competitor1NbRedCards,
        competitor2NbRedCards,
        areMatchLineUpsAvailable,
      };
    },
  );
};

export default makeRowFactorySelector;
